import Vue from 'vue';
import Router from 'vue-router';
import store from '@/store';
import { authService } from '@/services/auth.service';
import { resolveMustChangePasswordFlag } from '@/utils/passwordFlags';
import { hasAnyRole, ROLE_ADMIN, ROLE_MASTER } from '@/utils/roleHelpers';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      name: 'Login',
      component: () => import('@/views/LoginView.vue'),
      meta: { 
        requiresAuth: false,
        redirectIfAuthenticated: true 
      },
    },
    {
      path: '/login',
      redirect: '/',
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: () => import('@/views/DashboardView.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'Home',
          component: () => import('@/views/HomeView.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'events',
          name: 'Events',
          component: () => import('@/views/Events/EventsView.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'events/test',
          name: 'Tests',
          component: () => import('@/views/EventsTest/EventTestView.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'calendar',
          name: 'Calendar',
          component: () => import('@/views/Calendar/CalendarView.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'national-records',
          name: 'NationalRecords',
          component: () => import('@/views/NationalRecords/NationalRecordsUploadView.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'rankings',
          name: 'Rankings',
          component: () => import('@/views/Rankings/RankingsView.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'profile',
          name: 'Profile',
          component: () => import('@/views/Profile/ProfileView.vue'),
          meta: { requiresAuth: true },
        },
        {
          path: 'users',
          name: 'Users',
          component: () => import('@/views/Users/UsersView.vue'),
          meta: { requiresAuth: true, allowedRoles: [ROLE_MASTER, ROLE_ADMIN] },
        },
        {
          path: 'maintenance',
          name: 'Maintenance',
          component: () => import('@/views/Maintenance/MaintenanceView.vue'),
          meta: { requiresAuth: true, allowedRoles: [ROLE_MASTER, ROLE_ADMIN] },
        },
      ],
    },
    // Ruta 404
    {
      path: '*',
      name: 'NotFound',
      redirect: '/',
    },
  ],
});

// Guard de navegación más robusto
router.beforeEach(async (to, from, next) => {
  // Actualizar el estado del token en el store
  store.dispatch('auth/updateTokenStatus');

  const isAuthenticated = authService.isAuthenticated();
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const redirectIfAuthenticated = to.matched.some((record) => record.meta.redirectIfAuthenticated);
  const allowedRoleGroups = to.matched
    .filter((record) => record.meta?.allowedRoles)
    .map((record) => record.meta.allowedRoles)
    .flat();
  const user = authService.getUser();
  const mustChangePassword = isAuthenticated && resolveMustChangePasswordFlag(user);

  // Log para debugging
  console.log('🔒 Router Guard:', {
    to: to.path,
    isAuthenticated,
    requiresAuth,
    redirectIfAuthenticated,
    mustChangePassword,
    tokenTimeRemaining: authService.getTokenTimeRemaining()
  });

  if (allowedRoleGroups.length && !hasAnyRole(user, allowedRoleGroups)) {
    console.warn('🚫 Acceso denegado: rol sin permisos para esta ruta');
    Vue.$toast?.error('No tienes permisos para acceder a este módulo');
    next({ path: '/dashboard' });
    return;
  }

  if (requiresAuth) {
    if (!isAuthenticated) {
      console.warn('🚫 Acceso denegado: Usuario no autenticado');
      Vue.$toast.warning('Debes iniciar sesión para acceder a esta página');
      next({ path: '/' });
      return;
    }

    // Verificar si el token está próximo a expirar (menos de 5 minutos)
    if (authService.isTokenExpiringSoon()) {
      const timeRemaining = authService.getTokenTimeRemaining();
      Vue.$toast.warning(`Tu sesión expirará en ${timeRemaining} minutos`);
    }
  }

  if (redirectIfAuthenticated && isAuthenticated) {
    console.log('🔄 Redirigiendo usuario autenticado al dashboard');
    next({ path: '/dashboard' });
    return;
  }

  next();
});

// Guard después de cada navegación
router.afterEach((to) => {
  // Actualizar título de la página
  const pageTitle = to.meta?.title || 'FDPA - Sistema Stivou';
  document.title = pageTitle;

  // Log para tracking
  console.log('📍 Navegación completada:', to.path);
});

export default router;